import { faEnvelopeOpenText, faLock } from '@fortawesome/pro-duotone-svg-icons'

import AppLayout from '../../components/app-layout/AppLayout'
import Icon from '../../components/icon/Icon'
import PingCard from '../../components/ping-card/PingCard'
import { useQuickPassPings } from '../../_hooks/use-quickpass-pings'
import {
  PageStack,
  Header,
  PageTitle,
  PageSubtitle,
  PrivacyBanner,
  PrivacyGlyph,
  Grid,
  EmptyCard,
  EmptyEmoji,
  EmptyTitle,
  EmptyBody,
  ErrorBanner,
  StatusText,
} from './styled'

/**
 * Renter-side Pings index. Anonymous nudges from property managers and
 * realtors about specific listings — only delivered when QuickPass
 * thinks the listing is a real match for the renter. Identity stays
 * hidden until the renter chooses to engage.
 *
 * @returns {JSX.Element}
 */
const PingsPage = (): JSX.Element => {
  const { state, pings, errorMessage, markViewed, respond } =
    useQuickPassPings()

  return (
    <AppLayout contentMaxWidth="standard">
      <PageStack
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.32 } }}
      >
        <Header>
          <PageTitle>Pings</PageTitle>
          <PageSubtitle>
            Anonymous match-only nudges from agents and property managers. They
            send a listing — we only deliver it if it actually fits what
            you&apos;re looking for. They never see who you are unless you say
            you&apos;re interested.
          </PageSubtitle>
        </Header>

        <PrivacyBanner>
          <PrivacyGlyph aria-hidden="true">
            <Icon icon={faLock} />
          </PrivacyGlyph>
          You&apos;re anonymous to senders. Pass anything that doesn&apos;t
          land.
        </PrivacyBanner>

        {state === 'loading' ? (
          <StatusText>Pulling fresh pings…</StatusText>
        ) : null}

        {state === 'error' ? <ErrorBanner>{errorMessage}</ErrorBanner> : null}

        {state === 'ready' && pings.length === 0 ? (
          <EmptyCard>
            <EmptyEmoji aria-hidden="true">
              <Icon icon={faEnvelopeOpenText} label="Inbox" />
            </EmptyEmoji>
            <EmptyTitle>No pings yet</EmptyTitle>
            <EmptyBody>
              When an agent or property manager sends a ping that lines up with
              your preferences, it lands here. Tighten your cities, budget, and
              bedrooms in your profile so we deliver the right matches.
            </EmptyBody>
          </EmptyCard>
        ) : null}

        {pings.length > 0 ? (
          <Grid>
            {pings.map(ping => (
              <PingCard
                key={ping.id}
                ping={ping}
                onView={markViewed}
                onRespond={respond}
              />
            ))}
          </Grid>
        ) : null}
      </PageStack>
    </AppLayout>
  )
}

export default PingsPage
